import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Table,
  Badge, Spinner, Alert, Nav
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import Navbar from '../components/Navbar';
import reportService from '../services/reportService';
import usePagination from '../hooks/usePagination';
import TablePagination from '../components/TablePagination';

ChartJS.register(
  ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend
);

// ─── Shared constants ───────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const PALETTE = [
  '#4472C4','#ED7D31','#A9D18E','#FF4B4B','#FFC000',
  '#5B9BD5','#70AD47','#9467BD','#FF7F7F','#8FAADC',
  '#FFD966','#FF99CC','#BDD7EE','#C9C9C9','#C00000',
];

const ADNOC_COLOR   = '#0d6efd';
const NONADNOC_COLOR = '#fd7e14';
const TOTAL_COLOR   = '#198754';

const CHART_H = 320;  // px height for chart containers

const stackedOpts = (title) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' }, title: { display: !!title, text: title } },
  scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } }
});

const groupedOpts = (title) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' }, title: { display: !!title, text: title } },
  scales: { y: { beginAtZero: true } }
});

const lineOpts = (title) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' }, title: { display: !!title, text: title } },
  scales: { y: { beginAtZero: true } }
});

const pieOpts = (title) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'right' }, title: { display: !!title, text: title } }
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, bg = 'primary', text = 'white' }) {
  return (
    <Card className="text-center mb-3" bg={bg} text={text}>
      <Card.Body className="py-3">
        <div className="small fw-semibold">{label}</div>
        <h3 className="mb-0 fw-bold">{value ?? '—'}</h3>
      </Card.Body>
    </Card>
  );
}

function ChartCard({ title, height = CHART_H, children }) {
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">{title}</Card.Header>
      <Card.Body>
        <div style={{ height }}>{children}</div>
      </Card.Body>
    </Card>
  );
}

function LoadingBox() {
  return (
    <div className="text-center py-5">
      <Spinner animation="border" variant="primary" />
      <p className="mt-2 text-muted">Loading…</p>
    </div>
  );
}

function NoData() {
  return <p className="text-center text-muted py-4">No data available for the selected period.</p>;
}

/** Build multi-series dataset from {name, monthly[]} array */
function buildSeriesDataset(seriesArr, months) {
  return {
    labels: months,
    datasets: seriesArr.map((s, i) => ({
      label: s.name,
      data: s.monthly,
      backgroundColor: PALETTE[i % PALETTE.length] + 'CC',
      borderColor: PALETTE[i % PALETTE.length],
      borderWidth: 1,
    })),
  };
}

/** Build ADNOC / Non-ADNOC monthly bar dataset */
function buildAdnocDataset(monthly, months) {
  return {
    labels: months,
    datasets: [
      {
        label: 'ADNOC',
        data: monthly.map(m => m.adnoc),
        backgroundColor: ADNOC_COLOR + 'CC',
        borderColor: ADNOC_COLOR, borderWidth: 1,
      },
      {
        label: 'Non-ADNOC',
        data: monthly.map(m => m.non_adnoc),
        backgroundColor: NONADNOC_COLOR + 'CC',
        borderColor: NONADNOC_COLOR, borderWidth: 1,
      },
    ],
  };
}

// ─── Year selector ───────────────────────────────────────────────────────────
function YearSelector({ year, onChange }) {
  return (
    <Form.Group className="d-flex align-items-center gap-2 mb-0">
      <Form.Label className="mb-0 fw-semibold">Year:</Form.Label>
      <Form.Select
        value={year}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: 110 }}
      >
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </Form.Select>
    </Form.Group>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB SECTIONS
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1 & 2: Monthly Inspections (shared component, ndt flag) ─────────────────
function MonthlyInspTab({ year, ndt }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('equipment');

  useEffect(() => {
    setData(null);
  }, [year, ndt]);

  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getMonthlyInspections(year, ndt)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, ndt, data, loading]);

  const { months, monthly, summary, by_inspector, by_client, by_equipment } = data || {};

  const seriesMap = { inspector: by_inspector, client: by_client, equipment: by_equipment };
  const series = seriesMap[groupBy] || [];

  const { page: seriesPage, setPage: setSeriesPage, totalPages: seriesTotalPages, paginatedItems: paginatedSeries } = usePagination(series);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const adnocDataset = buildAdnocDataset(monthly, months);
  const seriesDataset = series.length > 0 ? buildSeriesDataset(series, months) : null;

  return (
    <>
      {/* KPI row */}
      <Row className="mb-3">
        <Col md={3}><KpiCard label="Total" value={summary.total} bg="dark" /></Col>
        <Col md={3}><KpiCard label="ADNOC" value={summary.adnoc} bg="primary" /></Col>
        <Col md={3}><KpiCard label="Non-ADNOC" value={summary.non_adnoc} bg="warning" text="dark" /></Col>
        <Col md={3}><KpiCard label="ADNOC %" value={summary.total ? Math.round(summary.adnoc / summary.total * 100) + '%' : '0%'} bg="info" /></Col>
      </Row>

      {/* ADNOC vs Non-ADNOC monthly bar */}
      <Row className="mb-4">
        <Col md={12}>
          <ChartCard title="Monthly — ADNOC vs Non-ADNOC">
            <Bar data={adnocDataset} options={stackedOpts('')} />
          </ChartCard>
        </Col>
      </Row>

      {/* Group-by breakdown */}
      <Row className="mb-2 align-items-center">
        <Col>
          <h6 className="mb-0 fw-semibold">Breakdown by:</h6>
        </Col>
        <Col xs="auto">
          <Form.Select value={groupBy} onChange={e => { setGroupBy(e.target.value); setSeriesPage(1); }} style={{ width: 160 }}>
            <option value="equipment">Equipment Type</option>
            <option value="inspector">Inspector</option>
            <option value="client">Client</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          {seriesDataset ? (
            <ChartCard title={`Monthly — by ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} (stacked)`}>
              <Bar data={seriesDataset} options={stackedOpts('')} />
            </ChartCard>
          ) : <NoData />}
        </Col>
      </Row>

      {/* Summary table */}
      {series.length > 0 && (
        <Card>
          <Card.Header className="fw-semibold">Summary Table</Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</th>
                    {months.map(m => <th key={m}>{m}</th>)}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSeries.map((s, i) => (
                    <tr key={i}>
                      <td>{s.name}</td>
                      {s.monthly.map((v, j) => <td key={j}>{v || ''}</td>)}
                      <td><strong>{s.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination currentPage={seriesPage} totalPages={seriesTotalPages} onPageChange={setSeriesPage} />
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

// ─── 3: Clients Served ────────────────────────────────────────────────────────
function ClientsServedTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getClientsServed(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const { monthly, yearly, total_this_year } = data;

  const monthlyChart = {
    labels: monthly.map(m => m.month_name),
    datasets: [{
      label: 'Unique Clients',
      data: monthly.map(m => m.unique_clients),
      borderColor: ADNOC_COLOR, backgroundColor: ADNOC_COLOR + '33',
      tension: 0.3, fill: true, pointRadius: 5,
    }],
  };

  const yearlyChart = {
    labels: yearly.map(y => String(y.year)),
    datasets: [{
      label: 'Unique Clients',
      data: yearly.map(y => y.unique_clients),
      backgroundColor: TOTAL_COLOR + 'CC', borderColor: TOTAL_COLOR, borderWidth: 1,
    }],
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={4}><KpiCard label={`Unique Clients in ${year}`} value={total_this_year} bg="primary" /></Col>
      </Row>
      <Row className="mb-4">
        <Col md={8}>
          <ChartCard title={`Monthly Unique Clients — ${year}`}>
            <Line data={monthlyChart} options={lineOpts('')} />
          </ChartCard>
        </Col>
        <Col md={4}>
          <ChartCard title="Yearly Unique Clients">
            <Bar data={yearlyChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
      </Row>

      {/* Monthly detail table */}
      <Card>
        <Card.Header className="fw-semibold">Monthly Client Detail</Card.Header>
        <Card.Body>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr><th>Month</th><th>Unique Clients</th><th>Clients</th></tr>
              </thead>
              <tbody>
                {monthly.map((m, i) => (
                  <tr key={i}>
                    <td>{m.month_name}</td>
                    <td><Badge bg="primary">{m.unique_clients}</Badge></td>
                    <td>
                      {m.client_names.map((cn, j) => (
                        <Badge key={j} bg="secondary" className="me-1">{cn}</Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}

// ─── 4: Inspector Utilisation ──────────────────────────────────────────────────
function InspectorUtilisationTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getInspectorUtilization(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { months, inspectors, years, yearly_data } = data || {};

  const { page: inspPage, setPage: setInspPage, totalPages: inspTotalPages, paginatedItems: paginatedInspectors } = usePagination(inspectors || []);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const monthlyChart = inspectors.length > 0
    ? buildSeriesDataset(inspectors, months) : null;

  const yearlyChart = yearly_data.length > 0 && years.length > 0 ? {
    labels: years.map(String),
    datasets: yearly_data.map((s, i) => ({
      label: s.name,
      data: s.yearly,
      backgroundColor: PALETTE[i % PALETTE.length] + 'CC',
      borderColor: PALETTE[i % PALETTE.length], borderWidth: 1,
    })),
  } : null;

  return (
    <>
      <Row className="mb-3">
        <Col md={3}><KpiCard label="Total Inspectors" value={inspectors.length} bg="primary" /></Col>
        <Col md={3}><KpiCard label={`Total Inspections ${year}`} value={inspectors.reduce((a, s) => a + s.total, 0)} bg="success" /></Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          {monthlyChart
            ? <ChartCard title={`Monthly Inspector Utilisation — ${year} (stacked)`}>
                <Bar data={monthlyChart} options={stackedOpts('')} />
              </ChartCard>
            : <NoData />}
        </Col>
      </Row>

      {yearlyChart && (
        <Row className="mb-4">
          <Col md={12}>
            <ChartCard title="Annual Inspector Utilisation (last 5 years)">
              <Bar data={yearlyChart} options={groupedOpts('')} />
            </ChartCard>
          </Col>
        </Row>
      )}

      {/* Detail table */}
      <Card>
        <Card.Header className="fw-semibold">Inspector Details — {year}</Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Inspector</th><th>Role</th>
                  {months.map(m => <th key={m}>{m}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInspectors.map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td>
                      <Badge bg={s.role === 'jr_inspector' ? 'primary' : 'success'}>
                        {s.role === 'jr_inspector' ? 'Inspector' : 'Reviewer'}
                      </Badge>
                    </td>
                    {s.monthly.map((v, j) => <td key={j}>{v || ''}</td>)}
                    <td><strong>{s.total}</strong></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <TablePagination currentPage={inspPage} totalPages={inspTotalPages} onPageChange={setInspPage} />
          </div>
        </Card.Body>
      </Card>
    </>
  );
}

// ─── 5: Certificates Issued ───────────────────────────────────────────────────
function CertificatesIssuedTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getCertificatesIssued(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { monthly, yearly, by_client, total_this_year } = data || {};

  const { page: clientPage, setPage: setClientPage, totalPages: clientTotalPages, paginatedItems: paginatedByClient } = usePagination(by_client || []);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const monthlyChart = {
    labels: monthly.map(m => m.month_name),
    datasets: [{
      label: 'Certificates Issued',
      data: monthly.map(m => m.count),
      backgroundColor: TOTAL_COLOR + 'CC', borderColor: TOTAL_COLOR, borderWidth: 1,
    }],
  };

  const yearlyChart = {
    labels: yearly.map(y => String(y.year)),
    datasets: [{
      label: 'Certificates Issued',
      data: yearly.map(y => y.count),
      backgroundColor: ADNOC_COLOR + 'CC', borderColor: ADNOC_COLOR, borderWidth: 1,
    }],
  };

  const pieData = by_client.length > 0 ? {
    labels: by_client.map(c => c.client_name),
    datasets: [{
      data: by_client.map(c => c.count),
      backgroundColor: by_client.map((_, i) => PALETTE[i % PALETTE.length] + 'CC'),
      borderColor: by_client.map((_, i) => PALETTE[i % PALETTE.length]),
      borderWidth: 1,
    }],
  } : null;

  return (
    <>
      <Row className="mb-3">
        <Col md={3}><KpiCard label={`Certificates in ${year}`} value={total_this_year} bg="success" /></Col>
        <Col md={3}><KpiCard label="Total All Time" value={by_client.reduce((a, c) => a + c.count, 0)} bg="primary" /></Col>
      </Row>

      <Row className="mb-4">
        <Col md={7}>
          <ChartCard title={`Monthly Certificates Issued — ${year}`}>
            <Bar data={monthlyChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
        <Col md={5}>
          <ChartCard title="Yearly Certificates Issued">
            <Bar data={yearlyChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
      </Row>

      {pieData && (
        <Row className="mb-4">
          <Col md={6}>
            <ChartCard title="Certificates by Customer (All Time)" height={280}>
              <Pie data={pieData} options={pieOpts('')} />
            </ChartCard>
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="fw-semibold">Customer Breakdown</Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead><tr><th>#</th><th>Customer</th><th>Certificates</th></tr></thead>
                    <tbody>
                      {paginatedByClient.map((c, i) => (
                        <tr key={i}>
                          <td>{(clientPage - 1) * 10 + i + 1}</td>
                          <td>{c.client_name}</td>
                          <td><Badge bg="success">{c.count}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <TablePagination currentPage={clientPage} totalPages={clientTotalPages} onPageChange={setClientPage} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}


// ─── 7: Certificates on Hold ──────────────────────────────────────────────────
function OnHoldTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getCertificatesOnHold(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { monthly, yearly, total_on_hold, details } = data || {};

  const { page: holdPage, setPage: setHoldPage, totalPages: holdTotalPages, paginatedItems: paginatedDetails } = usePagination(details || []);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const monthlyChart = {
    labels: monthly.map(m => m.month_name),
    datasets: [{
      label: 'On Hold',
      data: monthly.map(m => m.count),
      backgroundColor: '#dc3545CC', borderColor: '#dc3545', borderWidth: 1,
    }],
  };

  const yearlyChart = {
    labels: yearly.map(y => String(y.year)),
    datasets: [{
      label: 'On Hold',
      data: yearly.map(y => y.count),
      backgroundColor: '#fd7e14CC', borderColor: '#fd7e14', borderWidth: 1,
    }],
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={3}>
          <KpiCard label="Currently On Hold (Total)" value={total_on_hold} bg="danger" />
        </Col>
        <Col md={3}>
          <KpiCard label={`New On Hold in ${year}`} value={monthly.reduce((a, m) => a + m.count, 0)} bg="warning" text="dark" />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={7}>
          <ChartCard title={`Monthly On Hold — ${year}`}>
            <Bar data={monthlyChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
        <Col md={5}>
          <ChartCard title="Yearly On Hold">
            <Bar data={yearlyChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
      </Row>

      {details.length > 0 && (
        <Card>
          <Card.Header className="fw-semibold text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Current On Hold Certificates ({total_on_hold})
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Work Order No</th>
                    <th>Certificate No</th>
                    <th>Client</th>
                    <th>Form Type</th>
                    <th>Equipment</th>
                    <th>Inspector</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDetails.map((d, i) => (
                    <tr key={i}>
                      <td>{(holdPage - 1) * 10 + i + 1}</td>
                      <td>{d.work_order_no}</td>
                      <td>{d.certificate_number}</td>
                      <td>{d.client_name}</td>
                      <td><Badge bg="primary">{d.form_type_code}</Badge></td>
                      <td>{d.equipment_type_code}</td>
                      <td>{d.inspector_name}</td>
                      <td>{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination currentPage={holdPage} totalPages={holdTotalPages} onPageChange={setHoldPage} />
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

// ─── 8: Work Order Status ─────────────────────────────────────────────────────
function WorkOrdersTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    reportService.getWorkOrdersStatus(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { work_orders } = data || {};
  const filtered = (work_orders || []).filter(w =>
    !search ||
    w.work_order_no.toLowerCase().includes(search.toLowerCase()) ||
    w.client_name.toLowerCase().includes(search.toLowerCase())
  );

  const { page: woPage, setPage: setWoPage, totalPages: woTotalPages, paginatedItems: paginatedWorkOrders } = usePagination(filtered);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const totals = {
    prepared: work_orders.reduce((a, w) => a + w.total_prepared, 0),
    ready: work_orders.reduce((a, w) => a + w.ready_for_review, 0),
    released: work_orders.reduce((a, w) => a + w.released, 0),
    on_hold: work_orders.reduce((a, w) => a + w.on_hold, 0),
    in_progress: work_orders.reduce((a, w) => a + w.in_progress, 0),
  };

  const summaryChart = {
    labels: ['Prepared', 'Ready for Review', 'Released', 'On Hold', 'In Progress'],
    datasets: [{
      label: 'Certificates',
      data: [totals.prepared, totals.ready, totals.released, totals.on_hold, totals.in_progress],
      backgroundColor: [
        '#0d6efd99','#ffc10799','#19875499','#dc354599','#6c757d99',
      ],
      borderColor: ['#0d6efd','#ffc107','#198754','#dc3545','#6c757d'],
      borderWidth: 1,
    }],
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={2}><KpiCard label="Work Orders" value={work_orders.length} bg="dark" /></Col>
        <Col md={2}><KpiCard label="Prepared" value={totals.prepared} bg="primary" /></Col>
        <Col md={2}><KpiCard label="Ready for Review" value={totals.ready} bg="warning" text="dark" /></Col>
        <Col md={2}><KpiCard label="Released" value={totals.released} bg="success" /></Col>
        <Col md={2}><KpiCard label="On Hold" value={totals.on_hold} bg="danger" /></Col>
        <Col md={2}><KpiCard label="In Progress" value={totals.in_progress} bg="secondary" /></Col>
      </Row>

      <Row className="mb-4">
        <Col md={5}>
          <ChartCard title={`Work Order Status Summary — ${year}`}>
            <Bar data={summaryChart} options={groupedOpts('')} />
          </ChartCard>
        </Col>
        <Col md={7}>
          <Card className="h-100">
            <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
              <span>Work Order Detail ({work_orders.length} work orders)</span>
              <Form.Control
                size="sm"
                placeholder="Search WO / Client…"
                value={search}
                onChange={e => { setSearch(e.target.value); setWoPage(1); }}
                style={{ width: 200 }}
              />
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Work Order</th>
                      <th>Client</th>
                      <th>Prepared</th>
                      <th>Ready</th>
                      <th>Released</th>
                      <th>On Hold</th>
                      <th>In Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedWorkOrders.map((w, i) => (
                      <tr key={i}>
                        <td><strong>{w.work_order_no}</strong></td>
                        <td>{w.client_name}</td>
                        <td><Badge bg="primary">{w.total_prepared}</Badge></td>
                        <td>{w.ready_for_review > 0 && <Badge bg="warning" text="dark">{w.ready_for_review}</Badge>}</td>
                        <td>{w.released > 0 && <Badge bg="success">{w.released}</Badge>}</td>
                        <td>{w.on_hold > 0 && <Badge bg="danger">{w.on_hold}</Badge>}</td>
                        <td>{w.in_progress > 0 && <Badge bg="secondary">{w.in_progress}</Badge>}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted">No results</td></tr>
                    )}
                  </tbody>
                </Table>
                <TablePagination currentPage={woPage} totalPages={woTotalPages} onPageChange={setWoPage} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}

// ─── LEGACY CHARTS (previously in Reports.js) ─────────────────────────────────
function LegacyChartsTab() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overviewStats, setOverviewStats] = useState(null);
  const [inspectorsList, setInspectorsList] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState('');
  const [inspectorPerformance, setInspectorPerformance] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [formsByEquipment, setFormsByEquipment] = useState([]);
  const [formsByFormType, setFormsByFormType] = useState([]);
  const [formsByStatus, setFormsByStatus] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateRangeData, setDateRangeData] = useState(null);
  const [loadingDateRange, setLoadingDateRange] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientDetails, setClientDetails] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [certificateStats, setCertificateStats] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [pendingForms, setPendingForms] = useState([]);
  const [pendingByInspector, setPendingByInspector] = useState([]);
  const [pendingInspectorFilter, setPendingInspectorFilter] = useState('');
  const [loadingPending, setLoadingPending] = useState(false);

  const {
    page: dateRangePage, setPage: setDateRangePage,
    totalPages: dateRangeTotalPages, paginatedItems: paginatedDateRangeSubmissions
  } = usePagination(dateRangeData?.submissions || []);

  const {
    page: inspectorsListPage, setPage: setInspectorsListPage,
    totalPages: inspectorsListTotalPages, paginatedItems: paginatedInspectorsList
  } = usePagination(inspectorsList);

  const {
    page: pendingFormsPage, setPage: setPendingFormsPage,
    totalPages: pendingFormsTotalPages, paginatedItems: paginatedPendingForms
  } = usePagination(pendingForms);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [ovRes, insRes, eqRes, ftRes, stRes, clRes, csRes, trRes, pfRes] = await Promise.all([
          reportService.getOverviewStats(),
          reportService.getInspectorsList(),
          reportService.getFormsByEquipment(),
          reportService.getFormsByFormType(),
          reportService.getFormsByStatus(),
          reportService.getClientsList(),
          reportService.getCertificateStats(),
          reportService.getMonthlyTrends(),
          reportService.getPendingForms(),
        ]);
        if (ovRes.success) setOverviewStats(ovRes.data);
        if (insRes.success) setInspectorsList(insRes.inspectors);
        if (eqRes.success) setFormsByEquipment(eqRes.data);
        if (ftRes.success) setFormsByFormType(ftRes.data);
        if (stRes.success) setFormsByStatus(stRes.data);
        if (clRes.success) setClientsList(clRes.clients);
        if (csRes.success) setCertificateStats(csRes.data);
        if (trRes.success) setMonthlyTrends(trRes.data);
        if (pfRes.success) { setPendingForms(pfRes.forms); setPendingByInspector(pfRes.by_inspector); }
      } catch (e) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleInspectorChange = async (e) => {
    const id = e.target.value;
    setSelectedInspector(id);
    if (!id) { setInspectorPerformance(null); return; }
    setLoadingPerformance(true);
    try {
      const res = await reportService.getInspectorPerformance(id);
      if (res.success) setInspectorPerformance(res);
    } catch (e) { console.error(e); }
    setLoadingPerformance(false);
  };

  const handleDateFilter = async () => {
    if (!fromDate || !toDate) { alert('Select both dates'); return; }
    setLoadingDateRange(true);
    try {
      const res = await reportService.getFormsByDateRange(fromDate, toDate);
      if (res.success) { setDateRangeData(res); setDateRangePage(1); }
    } catch (e) { alert(e.message || 'Error'); }
    setLoadingDateRange(false);
  };

  const handlePendingInspectorChange = async (e) => {
    const id = e.target.value;
    setPendingInspectorFilter(id);
    setLoadingPending(true);
    try {
      const res = await reportService.getPendingForms(id || null);
      if (res.success) { setPendingForms(res.forms); setPendingByInspector(res.by_inspector); setPendingFormsPage(1); }
    } catch (e) { console.error(e); }
    setLoadingPending(false);
  };

  const handleClientChange = async (e) => {
    const id = e.target.value;
    setSelectedClient(id);
    if (!id) { setClientDetails(null); return; }
    setLoadingClient(true);
    try {
      const res = await reportService.getClientDetails(id);
      if (res.success) setClientDetails(res);
    } catch (e) { console.error(e); }
    setLoadingClient(false);
  };

  if (loading) return <LoadingBox />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const chartOpts = { responsive: true, maintainAspectRatio: true, plugins: { legend: { position: 'top' } } };

  const eqData = {
    labels: formsByEquipment.map(i => i.equipment_type),
    datasets: [{ label: 'Forms', data: formsByEquipment.map(i => i.count),
      backgroundColor: formsByEquipment.map((_, i) => PALETTE[i % PALETTE.length] + 'CC'),
      borderColor: formsByEquipment.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 1 }]
  };
  const ftData = {
    labels: formsByFormType.map(i => i.form_type),
    datasets: [{ label: 'Forms', data: formsByFormType.map(i => i.count),
      backgroundColor: ADNOC_COLOR + 'CC', borderColor: ADNOC_COLOR, borderWidth: 1 }]
  };
  const statusColors = ['#0d6efd99','#ffc10799','#fd7e1499','#19875499'];
  const stData = {
    labels: formsByStatus.map(i => i.status),
    datasets: [{ label: 'Forms', data: formsByStatus.map(i => i.count),
      backgroundColor: statusColors, borderColor: statusColors.map(c => c.slice(0,7)), borderWidth: 1 }]
  };
  const trendData = {
    labels: monthlyTrends.map(i => i.month),
    datasets: [{ label: 'Forms Submitted', data: monthlyTrends.map(i => i.count),
      fill: false, borderColor: '#20c997', backgroundColor: '#20c99733', tension: 0.1 }]
  };

  return (
    <>
      {overviewStats && (
        <>
          <h5 className="mb-3">Overview Statistics</h5>
          <Row className="mb-4">
            <Col md={3}><Card className="text-center" bg="primary" text="white"><Card.Body><h6>Total Inspectors</h6><h2>{overviewStats.total_inspectors}</h2></Card.Body></Card></Col>
            <Col md={3}><Card className="text-center" bg="success" text="white"><Card.Body><h6>Total Forms</h6><h2>{overviewStats.total_forms}</h2></Card.Body></Card></Col>
            <Col md={3}><Card className="text-center" bg="warning" text="dark"><Card.Body><h6>Pending Forms</h6><h2>{overviewStats.pending_forms}</h2></Card.Body></Card></Col>
            <Col md={3}><Card className="text-center" bg="info" text="white"><Card.Body><h6>Certificates</h6><h2>{overviewStats.certificates_generated}</h2></Card.Body></Card></Col>
          </Row>
        </>
      )}

      <h5 className="mb-3">Forms Analysis</h5>
      <Row className="mb-4">
        <Col md={4}><Card><Card.Header><small>Forms by Equipment Type</small></Card.Header><Card.Body>{formsByEquipment.length > 0 ? <Pie data={eqData} options={chartOpts} /> : <NoData />}</Card.Body></Card></Col>
        <Col md={4}><Card><Card.Header><small>Forms by Type (ADNOC, ENAS, etc.)</small></Card.Header><Card.Body>{formsByFormType.length > 0 ? <Bar data={ftData} options={chartOpts} /> : <NoData />}</Card.Body></Card></Col>
        <Col md={4}><Card><Card.Header><small>Forms by Status</small></Card.Header><Card.Body>{formsByStatus.length > 0 ? <Pie data={stData} options={chartOpts} /> : <NoData />}</Card.Body></Card></Col>
      </Row>

      {monthlyTrends.length > 0 && (
        <Row className="mb-4">
          <Col md={12}><Card><Card.Header><small>Monthly Submission Trends</small></Card.Header><Card.Body><Line data={trendData} options={chartOpts} /></Card.Body></Card></Col>
        </Row>
      )}

      <h5 className="mb-3">Inspector Performance</h5>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header><small>Select Inspector to View Performance</small></Card.Header>
            <Card.Body>
              <Form.Select value={selectedInspector} onChange={handleInspectorChange} className="mb-3">
                <option value="">-- Select Inspector --</option>
                {inspectorsList.filter(i => i.role === 'jr_inspector').map(i => (
                  <option key={i.userid} value={i.userid}>{i.full_name} ({i.emp_code})</option>
                ))}
              </Form.Select>
              {loadingPerformance && <Spinner animation="border" size="sm" />}
              {inspectorPerformance && (
                <Row>
                  <Col md={3}><Card className="text-center" bg="primary" text="white"><Card.Body><small>Total Submitted</small><h3>{inspectorPerformance.performance.total_submitted}</h3></Card.Body></Card></Col>
                  <Col md={3}><Card className="text-center" bg="warning" text="dark"><Card.Body><small>Sent Back</small><h3>{inspectorPerformance.performance.sent_back_count}</h3></Card.Body></Card></Col>
                  <Col md={3}><Card className="text-center" bg="success" text="white"><Card.Body><small>Approved</small><h3>{inspectorPerformance.performance.approved_count}</h3></Card.Body></Card></Col>
                  <Col md={3}><Card className="text-center" bg="info" text="white"><Card.Body><small>Success Rate</small><h3>{inspectorPerformance.performance.success_rate}%</h3></Card.Body></Card></Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-3">Date Range Filter</h5>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header><small>Filter Forms by Inspection Date</small></Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}><Form.Group><Form.Label>From Date</Form.Label><Form.Control type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></Form.Group></Col>
                <Col md={4}><Form.Group><Form.Label>To Date</Form.Label><Form.Control type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></Form.Group></Col>
                <Col md={4} className="d-flex align-items-end"><Button variant="primary" onClick={handleDateFilter} disabled={loadingDateRange} className="mt-3">{loadingDateRange ? 'Loading…' : 'Apply Filter'}</Button></Col>
              </Row>
              {dateRangeData && (
                <div className="mt-3">
                  <h6>Results: {dateRangeData.total_count} forms found</h6>
                  <div className="mb-2">
                    <Badge bg="primary" className="me-1">Submitted: {dateRangeData.status_breakdown.submitted_by_junior || 0}</Badge>
                    <Badge bg="warning" className="me-1">Sent Back: {dateRangeData.status_breakdown.sent_back_by_senior || 0}</Badge>
                    <Badge bg="info" className="me-1">Under Review: {dateRangeData.status_breakdown.reviewed_by_senior || 0}</Badge>
                    <Badge bg="success">Approved: {dateRangeData.status_breakdown.approved_by_admin || 0}</Badge>
                  </div>
                  {dateRangeData.submissions.length > 0 && (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead><tr><th>Form No</th><th>Client</th><th>Inspection Date</th><th>Submitted By</th><th>Status</th></tr></thead>
                        <tbody>
                          {paginatedDateRangeSubmissions.map(s => (
                            <tr key={s.submission_id}>
                              <td>{s.form_number}</td>
                              <td>{s.client_name}</td>
                              <td>{s.inspection_date}</td>
                              <td>{s.submitter_name}</td>
                              <td><Badge bg={s.current_status === 'approved_by_admin' ? 'success' : s.current_status === 'sent_back_by_senior' ? 'warning' : s.current_status === 'reviewed_by_senior' ? 'info' : 'primary'}>{s.current_status.replace(/_/g, ' ').toUpperCase()}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <TablePagination currentPage={dateRangePage} totalPages={dateRangeTotalPages} onPageChange={setDateRangePage} />
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-3">Client Reports</h5>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header><small>Select Client to View Details</small></Card.Header>
            <Card.Body>
              <Form.Select value={selectedClient} onChange={handleClientChange} className="mb-3">
                <option value="">-- Select Client --</option>
                {clientsList.map(c => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
              </Form.Select>
              {loadingClient && <Spinner animation="border" size="sm" />}
              {clientDetails && (
                <>
                  <Row className="mb-2">
                    <Col md={4}><Card className="text-center" bg="primary" text="white"><Card.Body><small>Total Forms</small><h3>{clientDetails.stats.total_forms}</h3></Card.Body></Card></Col>
                    <Col md={4}><Card className="text-center" bg="success" text="white"><Card.Body><small>Approved</small><h3>{clientDetails.stats.approved_forms}</h3></Card.Body></Card></Col>
                    <Col md={4}><Card className="text-center" bg="info" text="white"><Card.Body><small>Certs Sent</small><h3>{clientDetails.stats.certificates_sent}</h3></Card.Body></Card></Col>
                  </Row>
                  <div>{clientDetails.inspectors.map(i => <Badge key={i.userid} bg="secondary" className="me-1">{i.full_name} ({i.emp_code})</Badge>)}</div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-3">Registered Inspectors</h5>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header><small>All Inspectors ({inspectorsList.length})</small></Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead><tr><th>Name</th><th>Emp Code</th><th>Email</th><th>Role</th><th>Forms Submitted</th><th>Forms Reviewed</th><th>Registered</th></tr></thead>
                  <tbody>
                    {paginatedInspectorsList.map(i => (
                      <tr key={i.userid}>
                        <td>{i.full_name}</td><td>{i.emp_code}</td><td>{i.email}</td>
                        <td><Badge bg={i.role === 'jr_inspector' ? 'primary' : 'success'}>{i.role === 'jr_inspector' ? 'Inspector' : 'Reviewer'}</Badge></td>
                        <td>{i.forms_submitted}</td><td>{i.forms_reviewed}</td>
                        <td>{i.created_at ? new Date(i.created_at).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <TablePagination currentPage={inspectorsListPage} totalPages={inspectorsListTotalPages} onPageChange={setInspectorsListPage} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Pending Forms ─────────────────────────────────────── */}
      <h5 className="mb-3">
        Pending Forms
        {pendingForms.length > 0 && (
          <Badge bg="warning" text="dark" className="ms-2">{pendingForms.length}</Badge>
        )}
      </h5>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <small>Forms not yet approved — filter by inspector</small>
              <div style={{ width: 260 }}>
                <Form.Select
                  size="sm"
                  value={pendingInspectorFilter}
                  onChange={handlePendingInspectorChange}
                  disabled={loadingPending}
                >
                  <option value="">All Inspectors</option>
                  {pendingByInspector.map(ins => (
                    <option key={ins.inspector_id} value={ins.inspector_id}>
                      {ins.inspector_name} ({ins.count})
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loadingPending ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading…</div>
              ) : pendingForms.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-check-circle me-2 text-success"></i>No pending forms{pendingInspectorFilter ? ' for this inspector' : ''}.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Form Number</th>
                        <th>Work Order</th>
                        <th>Form Type</th>
                        <th>Equipment Type</th>
                        <th>Client</th>
                        <th>Inspector</th>
                        <th>Submitted On</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPendingForms.map(f => (
                        <tr key={f.submission_id}>
                          <td><small>{f.form_number || '—'}</small></td>
                          <td><small>{f.work_order_number || '—'}</small></td>
                          <td><small>{f.form_type}</small></td>
                          <td><small>{f.equipment_type}</small></td>
                          <td><small>{f.client_name}</small></td>
                          <td><small>{f.submitted_by_name}</small></td>
                          <td><small>{f.submitted_at}</small></td>
                          <td>
                            <Badge bg={
                              f.current_status === 'sent_back_by_senior' ? 'danger' :
                              f.current_status === 'reviewed_by_senior'  ? 'info'   : 'warning'
                            } text={f.current_status === 'reviewed_by_senior' ? 'white' : 'dark'}>
                              <small>{f.status_label}</small>
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <TablePagination currentPage={pendingFormsPage} totalPages={pendingFormsTotalPages} onPageChange={setPendingFormsPage} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {certificateStats && (
        <>
          <h5 className="mb-3">Certificate Statistics</h5>
          <Row className="mb-4">
            <Col md={4}><Card className="text-center" bg="primary" text="white"><Card.Body><h6>Total Generated</h6><h3>{certificateStats.total_generated}</h3></Card.Body></Card></Col>
            <Col md={4}><Card className="text-center" bg="success" text="white"><Card.Body><h6>Sent to Clients</h6><h3>{certificateStats.sent}</h3></Card.Body></Card></Col>
            <Col md={4}><Card className="text-center" bg="warning" text="dark"><Card.Body><h6>Pending Send</h6><h3>{certificateStats.pending}</h3></Card.Body></Card></Col>
          </Row>
        </>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'overview',    label: 'Overview & Forms' },
  { key: 'inspections', label: 'Monthly Inspections' },
  { key: 'ndt',         label: 'NDT Inspections' },
  { key: 'clients',     label: 'Clients Served' },
  { key: 'utilisation', label: 'Inspector Utilisation' },
  { key: 'issued',      label: 'Certificates Issued' },
  { key: 'onhold',      label: 'On Hold' },
  { key: 'workorders',  label: 'Work Orders' },
];

const InspectionReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [year, setYear] = useState(CURRENT_YEAR);

  const handleYearChange = useCallback((y) => setYear(y), []);

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':    return <LegacyChartsTab />;
      case 'inspections': return <MonthlyInspTab year={year} ndt={false} />;
      case 'ndt':         return <MonthlyInspTab year={year} ndt={true} />;
      case 'clients':     return <ClientsServedTab year={year} />;
      case 'utilisation': return <InspectorUtilisationTab year={year} />;
      case 'issued':      return <CertificatesIssuedTab year={year} />;
      case 'onhold':      return <OnHoldTab year={year} />;
      case 'workorders':  return <WorkOrdersTab year={year} />;
      default:            return null;
    }
  };

  return (
    <>
      <Navbar />
      <Container fluid className="mt-3 mb-5 px-4">
        {/* Header */}
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 className="mb-0">Equipment Inspection — Reports &amp; Analytics</h3>
          </Col>
          <Col xs="auto" className="d-flex gap-2 align-items-center">
            {activeTab !== 'overview' && <YearSelector year={year} onChange={handleYearChange} />}
            <Button variant="outline-secondary" size="sm" onClick={() => navigate('/reports')}>
              ← Back
            </Button>
          </Col>
        </Row>

        {/* Tab navigation */}
        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={k => setActiveTab(k)}
          className="mb-3 flex-nowrap"
          style={{ overflowX: 'auto', flexWrap: 'nowrap' }}
        >
          {TABS.map(t => (
            <Nav.Item key={t.key} style={{ whiteSpace: 'nowrap' }}>
              <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {/* Tab content */}
        <div>{renderTab()}</div>
      </Container>
    </>
  );
};

export default InspectionReports;
